import { useState, useCallback } from 'react';
import { Pencil, Info, CircleOff } from 'lucide-react';
import { Section, SectionHeader, SectionContent, Button } from '@predoc/cow';
import { format } from 'date-fns';
import { OPEN_STATUSES, RetrievalStatus } from '../../constants';
import {
  PatientRequest,
  ExistingPatient,
  PatientStatus,
  ProviderFacilityService,
  RequestDetails,
} from '../../types';
import {
  REQUEST_TYPE_STANDALONE_FACESHEET,
  REQUEST_TYPE_STANDALONE_RECORD_UPLOAD,
} from '../../types/request-services';
import { buildRequestDetailsFromStandalone } from '../../utils/buildStandaloneRequestDetails';
import { RequestDetailsDialog } from './RequestDetailsDialog';
import { CancelRequestDialog } from './CancelRequestDialog';
import { ProviderFacilityModal } from '../request-steps/request/suggested-providers-facility/ProviderFacilityModal';
import { ProviderFacilityFormData } from '../request-steps/request/suggested-providers-facility/validation';
import { ACTIONS, RetrievalMethod } from '../../../profiles/constants';
import { useNavigate } from 'react-router';
import { ServiceStatusBadge, ServiceActionsDropdown } from '../common';
import {
  useGetPatientRequestDetails,
  useGetStandaloneServiceDetails,
  useEditProviderFacilityRequest,
} from '../../hooks/patientsAPI';
import { calculateServiceDates } from '../../utils/mapRequestToApi';
import { mapProviderFacilityServiceToFormData } from '../../utils/mapPatientRequestDetailsToState';

interface PatientsListRequestSectionProps {
  request: PatientRequest;
  patient: ExistingPatient;
  showCancelButton?: boolean;
  showEditButton?: boolean;
}

const formatDate = (dateString: string) =>
  format(new Date(dateString), 'MM/dd/yyyy');

export const PatientsListRequestSection = ({
  request,
  patient,
  showCancelButton = true,
  showEditButton = true,
}: PatientsListRequestSectionProps) => {
  const navigate = useNavigate();
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<
    string | number | null
  >(null);

  const editProviderFacilityRequest = useEditProviderFacilityRequest();

  const requestId = request.crrId;
  const hasRequestId = typeof requestId === 'string' && requestId.length > 0;

  const isStandaloneRequest =
    request.requestType === REQUEST_TYPE_STANDALONE_FACESHEET ||
    request.requestType === REQUEST_TYPE_STANDALONE_RECORD_UPLOAD;
  const standaloneServiceId = request.services[0]
    ? String(request.services[0].id)
    : undefined;

  const requestedDate = formatDate(request.requestDate);

  const patientRequestId = request.crrId;
  const hasEditableProviderFacilityRetrieval = request.services.some(
    (service) =>
      service.retrievalMethod === RetrievalMethod.PROVIDER_FACILITY &&
      OPEN_STATUSES.includes(service.statusGroup as PatientStatus),
  );

  const canRedirectToEditProviderFacility =
    !!patient?.id && !!patientRequestId && hasEditableProviderFacilityRetrieval;

  const handleRedirectToEdit = () => {
    if (!canRedirectToEditProviderFacility) return;
    navigate(
      `/patients/edit/${encodeURIComponent(
        patient.id,
      )}?patientRequestId=${encodeURIComponent(
        patientRequestId,
      )}&step=1&substep=actions-details`,
    );
  };

  // Fetch request details when details dialog is open OR when editing a service
  const shouldFetchRequestDetails = isDetailsDialogOpen || !!editingServiceId;
  const requestDetailsRequestId =
    shouldFetchRequestDetails && !isStandaloneRequest
      ? request.crrId
      : undefined;
  const { data: requestDetailsData, isLoading: isRequestDetailsLoading } =
    useGetPatientRequestDetails(patient.id, requestDetailsRequestId);

  const { data: standaloneDetailsData, isLoading: isStandaloneDetailsLoading } =
    useGetStandaloneServiceDetails(
      patient.id,
      shouldFetchRequestDetails && isStandaloneRequest
        ? standaloneServiceId
        : undefined,
      request.requestType === REQUEST_TYPE_STANDALONE_FACESHEET ||
        request.requestType === REQUEST_TYPE_STANDALONE_RECORD_UPLOAD
        ? request.requestType
        : undefined,
    );

  // Same as CRR: only show modal when we have details (no placeholder, no show-while-loading)
  const detailsToShow: RequestDetails | null = isStandaloneRequest
    ? buildRequestDetailsFromStandalone(
        standaloneDetailsData,
        standaloneServiceId ?? '',
        request.requestDate,
        request.requestType ?? '',
      )
    : (requestDetailsData ?? null);
  const isDetailsLoading = isStandaloneRequest
    ? isStandaloneDetailsLoading
    : isRequestDetailsLoading;

  // Find the full service data from requestDetailsData when editing
  const editingService = editingServiceId
    ? requestDetailsData?.retrieveFrom?.find(
        (s) => s.id === editingServiceId,
      ) || null
    : null;

  const services = request.services.map((service) => {
    const isPPRetrieval =
      service.retrievalMethod === RetrievalMethod.PROVIDER_FACILITY;
    const isFacesheetService =
      service.retrievalMethod === RetrievalMethod.FACESHEETS;

    // - PP retrieval in OPEN status and Facesheet service in FS_PENDING status should show cancel option
    const isCancelable =
      hasRequestId &&
      ((isPPRetrieval && service.statusGroup === 'OPEN') ||
        (isFacesheetService && service.statusGroup === 'FS_PENDING'));

    const showEditOption =
      isPPRetrieval && service.statusGroup === RetrievalStatus.Open;

    // Show dropdown when edit actions are available
    const showActions = showEditOption;

    return {
      ...service,
      lastModified: formatDate(service.lastModified),
      isCancelable,
      showEditOption,
      showActions,
    };
  });

  // Enable cancel only when ALL services in the request are cancelable
  const allServicesCancelable =
    services.length > 0 && services.every((s) => s.isCancelable);

  const handleDetailsDialog = useCallback(
    (isOpen: boolean) => {
      setIsDetailsDialogOpen(isOpen);
    },
    [setIsDetailsDialogOpen],
  );

  const handleEditModal = (serviceId: string | number | null) => {
    setEditingServiceId(serviceId);
  };

  const handleSubmitEdit = (
    data: ProviderFacilityFormData & { id?: number },
  ) => {
    if (!data.id) {
      handleEditModal(null);
      return;
    }

    const { startDate, endDate } = calculateServiceDates(
      data.serviceDateRange,
      data.serviceStartDate,
      data.serviceEndDate,
    );

    editProviderFacilityRequest.mutate(
      {
        patientId: patient.id,
        request: {
          id: data.id,
          providerName: data.providerName || null,
          facilityName: data.facilityName || null,
          specialty: data.specialty || null,
          address1: data.address1 || null,
          address2: data.address2 || null,
          city: data.city || null,
          state: data.state || null,
          postalCode: data.postalCode || null,
          phoneNumber: data.phoneNumber || null,
          faxNumber: data.faxNumber || null,
          medicalRecordDetails: data.medicalRecordDetails || null,
          serviceStartDate: startDate,
          serviceEndDate: endDate,
        },
      },
      {
        onSuccess: () => {
          handleEditModal(null);
        },
      },
    );
  };

  return (
    <>
      <Section className="bg-white">
        <SectionHeader showDivider className="py-1.5">
          <span className="text-muted-foreground text-sm font-normal">
            Requested on {requestedDate}
          </span>
          <div className="flex items-center gap-2">
            {showCancelButton && (
              <CancelRequestDialog
                patientId={patient.id}
                requestId={requestId ?? ''}
              >
                {({ openDialog }) => (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={openDialog}
                    disabled={!allServicesCancelable}
                    aria-label="Cancel request"
                  >
                    <CircleOff className="text-destructive h-4 w-4" />
                  </Button>
                )}
              </CancelRequestDialog>
            )}
            {showEditButton && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleRedirectToEdit}
                disabled={!canRedirectToEditProviderFacility}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleDetailsDialog(true)}
            >
              <Info className="h-4 w-4" />
            </Button>
          </div>
        </SectionHeader>

        <SectionContent className="gap-0 p-0 pb-2">
          {/* Header Row */}
          <div className="grid grid-cols-[minmax(400px,1fr)_minmax(200px,300px)_150px_40px] gap-6 px-8 py-2">
            <div className="text-muted-foreground font-medium">Service</div>
            <div className="text-muted-foreground font-medium">Status</div>
            <div className="text-muted-foreground font-medium">
              Last Modified
            </div>
            <div></div>
          </div>

          {/* Service Rows */}
          {services.map((service) => (
            <div
              key={service.id}
              className="grid grid-cols-[minmax(400px,1fr)_minmax(200px,300px)_150px_40px] items-center gap-6 px-8 py-2"
            >
              <div className="flex items-center text-sm text-gray-900">
                {service.retrievalMethod === 'HIE'
                  ? ACTIONS[RetrievalMethod.HIE].displayName
                  : service.service}
              </div>
              <div className="flex min-w-0 items-center">
                <ServiceStatusBadge
                  statusGroup={service.statusGroup}
                  statusText={service.statusText}
                />
              </div>
              <div className="text-foreground flex items-center">
                {service.lastModified}
              </div>
              <div className="flex items-center justify-end">
                {service.showActions && (
                  <ServiceActionsDropdown
                    onEditClick={() => handleEditModal(service.id)}
                  />
                )}
              </div>
            </div>
          ))}
        </SectionContent>
      </Section>

      {isDetailsDialogOpen && detailsToShow && (
        <RequestDetailsDialog
          isOpen={isDetailsDialogOpen}
          onClose={() => handleDetailsDialog(false)}
          requestDetails={detailsToShow}
          patient={patient}
          isLoading={isDetailsLoading}
        />
      )}

      {editingServiceId && editingService && (
        <EditProviderFacilityModal
          service={editingService}
          onClose={() => handleEditModal(null)}
          onSubmit={handleSubmitEdit}
        />
      )}
    </>
  );
};

const EditProviderFacilityModal = ({
  service,
  onClose,
  onSubmit,
}: {
  service: ProviderFacilityService;
  onClose: () => void;
  onSubmit: (data: ProviderFacilityFormData) => void;
}) => {
  const initialValues = mapProviderFacilityServiceToFormData(service);

  return (
    <ProviderFacilityModal
      title="Edit Provider/Facility"
      submitButtonText="Save Changes"
      clearButtonText="Clear Form"
      showAddAnotherButton={false}
      initialValues={initialValues}
      onSubmit={onSubmit}
      open={!!service}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
};
